import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** `ink` is the SPEC_11 "Ink & Ember" preview theme — opt-in via the
 *  UserMenu theme picker (it is deliberately NOT in the toggle cycle).
 *  Promote to default only after live evaluation (SPEC_11 Task 6). */
type Theme = 'light' | 'dark' | 'ink';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
};

const STORAGE_KEY = 'pp-theme';
const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'ink') return stored;
  } catch {
    // Storage access can throw SecurityError under sandboxed or
    // privacy-restricted contexts; fall through to the default rather than
    // breaking app mount.
  }
  return 'light';
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  // `color-scheme` only accepts light/dark — ink is a dark-register theme,
  // so native form controls and scrollbars follow the dark scheme.
  document.documentElement.style.colorScheme = theme === 'ink' ? 'dark' : theme;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* localStorage may be disabled — fall through */
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  // The quick toggle cycles the two stable themes. From ink it returns to
  // light — leaving the preview is one click, re-entering is explicit.
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === 'light' ? 'dark' : 'light')),
    [],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
